import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { t } from 'i18next';
import PageToolbar from '../util/PageToolbar';
import SearchFilter, { applyColumnFilters, ActiveFilter } from '../util/SearchFilter';
import LoadingCenter from '../util/LoadingCenter';
import DataTable, { ColDef } from '../util/DataTable';
import HrLeaveDetailPanel from './HrLeaveDetailPanel';
import LeaveCarryoverPanel from './LeaveCarryoverPanel';
import SidePanel from '../util/SidePanel';
import ProfileCard from '../modal/ProfileCard';
import { formatDate } from '../util/formatDate';
import UserAvatar from '../util/UserAvatar';

interface Props {
  access: any;
  users: any[];
  departments?: any[];
  baseUrl: string;
  staticDateCompany?: string;
}

const HrLeaveBalanceTab: React.FC<Props> = ({ access, users, departments = [], baseUrl, staticDateCompany }) => {
  const [year, setYear] = useState<number | null>(null);
  const [data, setData] = useState<{ leaveYear: number | null; leaveYearStart: string | null; rows: any[] }>({ leaveYear: null, leaveYearStart: null, rows: [] });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<ActiveFilter[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [carryoverOpen, setCarryoverOpen] = useState(false);
  const [unitMode, setUnitMode] = useState<'auto' | 'days' | 'hours'>('auto');
  const [profileUserId, setProfileUserId] = useState<number | null>(null);

  const canAdjust = access?.hr_leave_edit_all === true || access?.hr_leave_edit_all === 1
    || access?.hr_leave_adjust === true || access?.hr_leave_adjust === 1;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = year !== null ? `?year=${year}` : '';
      const { data: res } = await axios.get(`hr-absence/balances${q}`);
      setData(res ?? { leaveYear: null, leaveYearStart: null, rows: [] });
      if (year === null && res?.leaveYear) setYear(res.leaveYear);
    } catch {
      // auth ошибки — глобальный interceptor
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  const leaveTypes = useMemo(() => {
    const first = data.rows.find(r => (r.balances ?? []).length);
    return (first?.balances ?? [])
      .map((b: any) => ({ id: b.typeId, name: b.typeName }))
      .sort((a: any, b: any) => (a.name ?? '').localeCompare(b.name ?? ''));
  }, [data.rows]);

  const sortedDepartments = useMemo(
    () => [...departments].sort((a, b) => (a.title ?? '').localeCompare(b.title ?? '')),
    [departments],
  );

  const patternOptions = useMemo(() => {
    const set = new Set<string>();
    data.rows.forEach((r: any) => { if (r.user.patternName) set.add(r.user.patternName); });
    return Array.from(set).sort().map(p => ({ value: p, label: p }));
  }, [data.rows]);

  // Поля-фильтры для токен-бара (не отображаются в таблице, только фильтруют).
  // «Type» — выбор отображаемого типа отпуска (по умолчанию основной, Annual).
  const filterFields: ColDef<any>[] = useMemo(() => [
    {
      key: 'department', label: t('Department') as string, filterType: 'select',
      filterExtract: (r: any) => String(r.user.departmentId ?? ''),
      filterOptions: sortedDepartments.map(d => ({ value: String(d.id), label: d.title })),
      render: () => null,
    },
    {
      key: 'pattern', label: t('Pattern') as string, filterType: 'select',
      filterExtract: (r: any) => r.user.patternName ?? '',
      filterOptions: patternOptions,
      render: () => null,
    },
    {
      key: 'leaveType', label: t('HrAbsenceType') as string, filterType: 'select',
      filterOptions: leaveTypes.map((tp: any) => ({ value: String(tp.id), label: tp.name })),
      filterExtract: () => '', render: () => null,
    },
  ], [sortedDepartments, patternOptions, leaveTypes]);

  // Выбранный тип для показа: из фильтра «Type», иначе основной (Annual)
  const selectedTypeId = filters.find(f => f.colKey === 'leaveType')?.value ?? null;

  const balanceOf = useCallback((row: any) => {
    const balances = row.balances ?? [];
    if (selectedTypeId) return balances.find((b: any) => String(b.typeId) === selectedTypeId) ?? null;
    return balances.find((b: any) => b.isDefault) ?? balances[0] ?? null;
  }, [selectedTypeId]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const bySearch = q
      ? data.rows.filter(r => `${r.user.first_name} ${r.user.last_name} ${r.user.job_title ?? ''}`.toLowerCase().includes(q))
      : data.rows;
    // Фильтр «leaveType» не сужает строки (он только переключает отображаемый тип)
    return applyColumnFilters(bySearch, filters, filterFields.filter(f => f.key !== 'leaveType'));
  }, [data.rows, search, filters, filterFields]);

  const selectedRow = useMemo(() => data.rows.find((r: any) => r.user.id === selectedId) ?? null, [data.rows, selectedId]);

  const num = (v: number | null | undefined) => (v === null || v === undefined ? '—' : v);
  // Единица показа: auto → из паттерна сотрудника; иначе принудительно
  const effUnit = (b: any) => unitMode === 'auto' ? (b?.patternUnit === 'hours' ? 'hours' : 'days') : unitMode;
  const cell = (b: any, days: number | null | undefined, hours: number | null | undefined) =>
    (days === null || days === undefined) ? '—'
      : effUnit(b) === 'hours' ? `${Math.round((hours ?? 0) * 10) / 10}${t('HShort')}` : `${days}${t('DShort')}`;

  const columns: ColDef<any>[] = useMemo(() => [
    {
      key: 'avatar', label: '', width: '48px', sortable: false,
      render: (r) => <UserAvatar user={r.user} baseUrl={baseUrl} size={34} />,
    },
    {
      key: 'name', label: t('Name') as string, width: '1.6fr',
      sortValue: r => `${r.user.first_name} ${r.user.last_name}`.toLowerCase(),
      render: r => (
        <div>
          <div style={{ fontSize: 13, fontWeight: 600 }}>{r.user.first_name} {r.user.last_name}</div>
          {r.user.job_title && <div style={{ fontSize: 11, color: 'var(--text-color-calendar-week)' }}>{r.user.job_title}</div>}
        </div>
      ),
    },
    {
      key: 'pattern', label: t('Pattern') as string, width: '1.2fr',
      sortValue: r => (r.user.patternName ?? '').toLowerCase(),
      render: r => <span style={{ fontSize: 12, color: 'var(--text-color-calendar)' }}>{r.user.patternName ?? '—'}</span>,
    },
    {
      key: 'remaining', label: t('HrRemaining') as string, align: 'right', width: '150px',
      sortValue: r => balanceOf(r)?.remainingDays ?? -1,
      render: r => {
        const b = balanceOf(r);
        const low = b && b.remainingDays !== null && b.remainingDays < 0;
        return <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 800, color: b && b.remainingDays !== null ? (low ? 'var(--color-danger)' : 'var(--color-success)') : 'var(--text-color)' }}>{cell(b, b?.remainingDays, b?.remainingHours)}</span>;
      },
    },
    {
      key: 'progress', label: t('HrUsage') as string, width: '140px', sortable: false,
      render: r => {
        const b = balanceOf(r);
        if (!b || b.entitlementDays === null || b.entitlementDays <= 0) return <span style={{ fontSize: 11, color: 'var(--text-color-calendar-week)' }}>—</span>;
        const pct = Math.min(100, Math.round((b.usedDays / b.entitlementDays) * 100));
        const low = (b.remainingDays ?? 0) < 0;
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ flex: 1, height: 7, borderRadius: 4, background: 'var(--bg-disable)', overflow: 'hidden' }}>
              <div style={{ height: '100%', borderRadius: 4, width: `${pct}%`, background: low ? 'var(--color-danger)' : 'linear-gradient(90deg,#2563eb,#7c3aed)', transition: 'width .4s' }} />
            </div>
            <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-color-calendar)', width: 34, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{pct}%</span>
          </div>
        );
      },
    },
  ], [balanceOf, baseUrl, unitMode]);

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
      <PageToolbar mb={0}>
        <SearchFilter columns={filterFields} search={search} onSearch={setSearch} filters={filters} onFilters={setFilters} />

        <div style={{ display: 'flex', gap: 4 }} title={t('HrUnitModeHint', 'Auto uses the unit from the working pattern') as string}>
          {(['auto', 'days', 'hours'] as const).map(m => (
            <button key={m} type="button" className={`dt-btn-ghost${unitMode === m ? ' active' : ''}`} style={{ fontSize: 11 }} onClick={() => setUnitMode(m)}>
              {m === 'auto' ? (t('Auto', 'Auto') as string) : m === 'days' ? (t('HrUnitDays') as string) : (t('HrUnitHours') as string)}
            </button>
          ))}
        </div>

        <div className="divider-v" />

        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <button type="button" className="dt-btn-ghost" style={{ padding: '6px 9px' }} title={t('Prev') as string}
            onClick={() => setYear(y => (y ?? new Date().getFullYear()) - 1)}>‹</button>
          <span style={{ fontSize: 12, fontWeight: 800, minWidth: 92, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>
            {data.leaveYear ?? year ?? ''}{data.leaveYearStart && !data.leaveYearStart.endsWith('-01-01') ? ` · ${formatDate(data.leaveYearStart, 'DD/MM')}` : ''}
          </span>
          <button type="button" className="dt-btn-ghost" style={{ padding: '6px 9px' }} title={t('Next') as string}
            onClick={() => setYear(y => (y ?? new Date().getFullYear()) + 1)}>›</button>
        </div>
        <div style={{ flex: 1 }} />
        {canAdjust && (
          <button type="button" className="dt-btn-ghost" onClick={() => { setSelectedId(null); setCarryoverOpen(true); }}>
            ↪ {t('HrCarryover', 'Rollover') as string}
          </button>
        )}
      </PageToolbar>

      <div style={{
        flex: 1, minHeight: 0, marginTop: 'var(--space-l1)', overflow: 'hidden',
        display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 440px', gap: 'var(--space-l1)',
      }}>
        {/* ── LEFT block (scrolls internally) ── */}
        <div className="ts-card" style={{ minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
          {loading ? (
            <LoadingCenter />
          ) : (
            <DataTable
              rows={filteredRows}
              columns={columns}
              getKey={r => r.user.id}
              onRowClick={r => { setCarryoverOpen(false); setSelectedId(r.user.id); }}
              selectedKey={selectedId}
              pagination={false}
              filterable={false}
              columnsToggle={false}
              exportable={false}
              hideToolbar
              noCard
              emptyText={t('Empty') as string}
            />
          )}
        </div>

        {/* ── RIGHT block (separate, scrolls internally, always visible) ── */}
        {carryoverOpen ? (
          <LeaveCarryoverPanel
            fromYear={(data.leaveYear ?? year ?? new Date().getFullYear()) - 1}
            onClose={() => setCarryoverOpen(false)}
            onDone={() => { load(); setCarryoverOpen(false); }}
            unitMode={unitMode}
          />
        ) : selectedRow ? (
          <HrLeaveDetailPanel
            onClose={() => setSelectedId(null)}
            row={selectedRow}
            year={data.leaveYear ?? year ?? new Date().getFullYear()}
            baseUrl={baseUrl}
            canAdjust={canAdjust}
            onChanged={load}
            staticDateCompany={staticDateCompany}
            unitMode={unitMode}
            onOpenProfile={setProfileUserId}
          />
        ) : (
          <div className="ts-card" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', minHeight: 0, textAlign: 'center', padding: 20 }}>
            <div style={{ color: 'var(--text-color-calendar-week)' }}>
              <div style={{
                width: 56, height: 56, borderRadius: '50%', margin: '0 auto 10px',
                background: 'var(--btn-user-form)', border: 'var(--border-input)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26, opacity: 0.7,
              }}>👤</div>
              <div style={{ fontSize: 13, fontWeight: 600 }}>{t('HrSelectEmployee')}</div>
            </div>
          </div>
        )}
      </div>

      <SidePanel open={profileUserId !== null} onClose={() => setProfileUserId(null)} width={760}>
        {profileUserId !== null && (() => {
          const pu = users.find((u: any) => u.id === profileUserId) || { id: profileUserId, first_name: '', last_name: '' };
          return (
            <ProfileCard
              isSelf={false}
              hrMode
              selectedUser={{ id: pu.id, first_name: pu.first_name, last_name: pu.last_name }}
              users={users}
              access={access}
              baseUrl={baseUrl}
              setShow={(v) => { if (!v) setProfileUserId(null); }}
              refreshUserHeder={() => {}}
              refreshUsers={() => { load(); }}
            />
          );
        })()}
      </SidePanel>
    </div>
  );
};

export default HrLeaveBalanceTab;
